// src/components/Intro/Intro.jsx
import React, { useEffect, useState } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import { motion } from "framer-motion";
import { HashLink } from "react-router-hash-link";

import slide1 from "../../assets/empty_stage.webp";
import slide2 from "../../assets/joyride_lava.webp";
import slide3 from "../../assets/joyride_live1.webp";
import slide4 from "../../assets/joyride_rummut.webp";
import slide5 from "../../assets/joyride_live3.webp";
import slide6 from "../../assets/Joyride_04.webp";

import logo from "../../assets/logo_500x500px_ei_harsoa.webp";

const OFFSET = 70;

const scrollWithOffset = (el) => {
  const y = el.getBoundingClientRect().top + window.pageYOffset - OFFSET;
  window.scrollTo({ top: y, behavior: "smooth" });
};

const slides = [
  {
    image: slide2,
    alt: "Bilebändi Joyride esiintymässä juhlavalaistuksessa"
  },
  {
    image: slide5,
    alt: "Bilebändi Joyride esiintyy täyden tanssilattian edessä"
  },
  {
    image: slide3,
    alt: "Bilebändi Joyriden kitaristi lavalla yleisön edessä"
  },
  {
    image: slide4,
    alt: "Bilebändi Joyriden rumpali keikalla savussa ja valossa"
  },
  {
    image: slide6,
    alt: "Bilebändi Joyride Hämeenlinnassa Hämeen Raatihuoneella"
  },
  {
    image: slide1,
    alt: "Bilebändi Joyriden lava ennen keikkaa"
  }
];

const testimonials = [
  "''Bilebändi Joyride toi erittäin hyvää tunnelmaa meidän yrityksemme pikkujouluihin. Tunnelma oli katossa ja bändi loistava.''",
  "''Kiitos teille mahtavasta tunnelmasta ja hyvästä meiningistä! Alusta asti viestiminen ja asioista sopiminen oli helppoa.''",
  "''Olitte enemmän kuin uskalsin toivoa! Erinomaisesti saitte yleisön mukaan ja loitte hyvän biletunnelman.''",
  "''Kiitos mahtavasta esiintymisestänne hääjuhlassamme! Kaikki meni tosi hienosti.''",
  "''Hyvän keikan soittivat häissämme. Hyväntuulinen ja osaava bilebändi.''",
    "''Kiitos huikeasta hääkeikasta! Tunnelma oli katossa ja tanssilattia täynnä, Joyride rokkasi!''",
  "''Kaikki kehuivat teitä ja fiilis säilyi korkealla loppuun asti. Valaistus ja miksaus menivät nappiin ja ylititte odotukset.''",
  "''Olitte tärkeä osa meidän ihanaa päivää ja aivan täydellinen valinta häihimme! Kehuja tuli joka suunnasta, ja ne halutaan välittää teille.''"
];

const Intro = () => {
  // Taustakuvien oma indeksi.
  const [currentSlide, setCurrentSlide] = useState(0);
  // Palautteita voi olla enemmän kuin taustakuvia.
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  useEffect(() => {
    AOS.init({ duration: 2000, easing: "ease-out-cubic" });

    if (window.gaEvent) {
      window.gaEvent("hero_nakyvissa");
    }

    const interval = setInterval(() => {
      // Vaihda myös taustakuva kuvien oman lukumäärän mukaan.
      setCurrentSlide((prev) => (prev + 1) % slides.length);
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const ga = (name, data = {}) => {
    if (window.gaEvent) window.gaEvent(name, data);
  };

  return (
    <div className="relative flex min-h-screen flex-col items-center overflow-hidden px-4 pb-6 pt-16 sm:block sm:h-screen sm:min-h-[720px] sm:p-0">
      {/* Taustakuvat */}
      {slides.map((slide, idx) => (
        <motion.div
          key={idx}
          className="absolute inset-0"
          initial={{ opacity: 0, scale: 1 }}
          animate={
            idx === currentSlide
              ? { opacity: 1, scale: 1.1 }
              : { opacity: 0, scale: 1 }
          }
          transition={{
            opacity: { duration: 1 },
            scale: { duration: 7, ease: "easeOut" }
          }}
        >
          <img
            src={slide.image}
            alt={slide.alt}
            className="h-full w-full object-cover"
            loading={idx === 0 ? "eager" : "lazy"}
            fetchPriority={idx === 0 ? "high" : "auto"}
            width="1920"
            height="1080"
          />

          <div className="pointer-events-none absolute inset-0 bg-black/55 bg-gradient-to-t from-black/95 via-black/30 to-black/25" />
        </motion.div>
      ))}

      {/* Mobiilissa sisältö vie oman tilansa; suuremmilla näytöillä säilyy alkuperäinen sijoittelu. */}
      <div className="relative z-20 flex w-full justify-center sm:absolute sm:inset-x-0 sm:top-[42%] sm:-translate-y-1/2 sm:px-4 md:top-[48%] lg:top-[44%] xl:top-[42%]">
        <motion.div
          className="flex w-full max-w-4xl flex-col items-center text-center"
          initial={{ opacity: 0, y: 42 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
        >
          {/* Negatiivinen alamarginaali tuo tekstin lähemmäs logoa mobiilissa. */}
          <img
            src={logo}
            alt="Bilebändi Joyride logo"
            width="500"
            height="500"
            fetchPriority="high"
            loading="eager"
            className="-mb-14 w-72 drop-shadow-2xl sm:mb-0 sm:w-80 md:w-[420px] lg:w-[470px]"
          />

          <p className="mt-3 max-w-2xl text-base font-semibold uppercase tracking-[0.1em] text-joyrideCyan drop-shadow-lg sm:text-lg sm:tracking-[0.18em] md:text-xl">
            Rock-henkinen bilebändi häihin, juhliin ja yritystapahtumiin
          </p>

          <div className="mt-6 flex w-full max-w-sm flex-col gap-3 sm:mt-7 sm:w-auto sm:max-w-none sm:flex-row">
            <HashLink
              to="/#varaa"
              scroll={scrollWithOffset}
              onClick={() => ga("hero_cta_klikattu", { kohde: "varaa" })}
              className="rounded-full bg-joyrideCyan px-7 py-3 text-sm font-bold uppercase tracking-[0.18em] text-joyrideNavy shadow-[0_0_28px_rgba(0,240,255,0.45)] transition hover:-translate-y-1 hover:bg-white hover:shadow-[0_0_36px_rgba(255,255,255,0.45)] focus:-translate-y-1 focus:outline-none"
            >
              Tarkista saatavuus
            </HashLink>

            <HashLink
              to="/#ohjelmisto"
              scroll={scrollWithOffset}
              onClick={() => ga("hero_cta_klikattu", { kohde: "ohjelmisto" })}
              className="rounded-full border border-white/40 bg-black/35 px-7 py-3 text-sm font-bold uppercase tracking-[0.18em] text-white shadow-lg backdrop-blur-sm transition hover:-translate-y-1 hover:border-joyrideCyan hover:text-joyrideCyan focus:-translate-y-1 focus:outline-none"
            >
              Tutustu ohjelmistoon
            </HashLink>
          </div>
        </motion.div>
      </div>

      {/* Palautteet */}
      <div className="relative z-30 mt-4 flex w-full max-w-sm flex-col sm:absolute sm:right-4 sm:top-20 sm:mt-0 sm:w-auto sm:max-w-none sm:items-end sm:space-y-2 md:right-10 lg:right-16">
        {/* Suuremmilla näytöillä otsikko säilyy kortin yläpuolella. */}
        <h3 className="hidden font-lobster text-lg text-white drop-shadow-lg sm:block md:text-xl">
          Asiakkaiden palautteita
        </h3>

        <div className="w-full rounded-2xl border border-white/20 bg-black/60 px-5 py-3 shadow-lg backdrop-blur-sm sm:w-56 sm:rotate-[-2deg] sm:bg-white/70 sm:px-4 sm:py-3 sm:dark:bg-black/60 md:max-w-sm lg:w-80">
          {/* Mobiilissa otsikko on kortin sisällä. */}
          <h3 className="mb-3 font-lobster text-lg text-white sm:hidden">
            Asiakkaiden palautteita
          </h3>

          {/* Sama ruudukon solu varaa korkeuden pisimmän palautteen mukaan. */}
          <div className="grid">
            {testimonials.map((testimonial, idx) => (
              <motion.p
                key={testimonial}
                aria-hidden={idx !== currentTestimonial}
                initial={false}
                animate={{ opacity: idx === currentTestimonial ? 1 : 0 }}
                transition={{ duration: 0.6 }}
                className="col-start-1 row-start-1 text-left text-sm italic leading-relaxed text-gray-100 sm:text-xs sm:leading-4 sm:text-gray-900 sm:dark:text-gray-100 md:text-sm lg:text-base"
              >
                {testimonial}
              </motion.p>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Intro;